export type SpaceInfo = {
  name: string
  url: string
  metadata: string
  metadataInfo: SpaceMetadata | undefined
  active: boolean
}

export type SpaceMetadata = {
  creator: ItemCreator
  title: string
  description: string
  icon: string
  thumbnail: string
  image: string
  logo: string
  name: string
  downloadLink: string
  techRequirements: {
    [key: string]: string
  }
  rules: any
  settings: {
    leaderboard: {
      enabled: boolean
    }
    login: {
      enabled: boolean
      method: 'redirect' | 'code'
    }
  }
  templates: { [key: string]: Template }
  feedSettings: {
    emailRequired: boolean
  }
}

/* -------------------------------------------------------------------------- */
/*                         JSON Template Schema Reader                        */
/* -------------------------------------------------------------------------- */
export type Preset = {
  [key: string]: number
}
export type Attribute = {
  [key: string]: SchemaProperty
}

export type SchemaProperty = {
  title: string
  description: string
  type: 'number' | 'list' | 'range' | 'enum' | 'boolean'
  values?: string[]
  required: boolean
  rules?: {
    min: number
    max: number
    keyName?: string
    unit?: string
    precision?: number
    tagSize?: {
      min: number
      max: number
    }
    lower?: {
      min: number
      max: number
      keyName?: number
    }
    upper?: {
      min: number
      max: number
      keyName?: number
    }
    questions: [
      {
        id: number
        question: string
        answers: [
          {
            response: string
            value: number
            nextQuestionId: string
            icon: string
          },
        ]
      },
    ]
    attributes?: { [key: string]: Attribute }
    presets?: { [key: string]: Preset }
  }
}

export interface SpaceLasersItem {
  creator: SchemaProperty
  name: SchemaProperty
  description: SchemaProperty
  asset: SchemaProperty
  tags: SchemaProperty[]
  properties: SchemaProperty[]
  rights: SchemaProperty
  royalties: SchemaProperty
  completedStepper: SchemaProperty
}

export interface ArtItem {
  creator: SchemaProperty
  name: SchemaProperty
  description: SchemaProperty
  asset: SchemaProperty
  rights: SchemaProperty
  royalties: SchemaProperty
  completedStepper: SchemaProperty
}

export interface ItemCreator {
  name: string
  link: string
  logo?: string
  credits?: string[]
}
export interface Template {
  address: string
  name: string
  shortDescription: string
  description: string
  image: string
  creator: ItemCreator
  techSpec: {
    [key: string]: string
  }
  displayProperties: { key: string; value: string }[]
  tags: string[]
  item: SpaceLasersItem | ArtItem
}
// TODO: Implement check for SchemaProperty structure (only after we have a proper schema for the templates finished and approved)
const isSchemaProperty = (property: any): property is SchemaProperty => true

// const isItemCreator = (creator: any): creator is ItemCreator =>
//   creator &&
//   typeof creator.name === 'string' &&
//   typeof creator.link === 'string' &&
//   (!creator.credits || Array.isArray(creator.credits))

const isSpaceLasersItem = (item: any): item is SpaceLasersItem =>
  item &&
  isSchemaProperty(item.creator) &&
  isSchemaProperty(item.name) &&
  isSchemaProperty(item.description) &&
  isSchemaProperty(item.asset) &&
  Array.isArray(item.tags) &&
  item.tags.every(isSchemaProperty) &&
  Array.isArray(item.properties) &&
  item.properties.every(isSchemaProperty)

const isArtItem = (item: any): item is ArtItem =>
  item &&
  isSchemaProperty(item.creator) &&
  isSchemaProperty(item.name) &&
  isSchemaProperty(item.description) &&
  isSchemaProperty(item.asset)

export const verifyTemplateSchema = (template: any): template is Template =>
  template &&
  typeof template.address === 'string' &&
  typeof template.name === 'string' &&
  typeof template.description === 'string' &&
  typeof template.image === 'string' &&
  // isItemCreator(template.creator) &&
  (isSpaceLasersItem(template.item) || isArtItem(template.item))
